import { useState, useEffect } from 'react';
import { trackUpgradePromptShown } from '../../lib/analytics';
import { usePremiumAccess } from '../../hooks/usePremiumAccess';
import { useBilling } from '../../hooks/useBilling';
import { useAuth } from '../../hooks/useAuth';
import ContactSalesModal from '../ContactSalesModal';
import { getEffectiveCurrency } from '../../lib/currency';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  buttonText?: string;
  icon?: React.ReactNode;
  tier?: 'pro' | 'enterprise';
}

export default function UpgradeModal({
  isOpen,
  onClose,
  title,
  description,
  buttonText = 'Start free trial',
  icon,
  tier = 'pro',
}: UpgradeModalProps) {
  const { isPaidPro, tempUnlocked, betaUnlocked } = usePremiumAccess();
  const { createCheckout } = useBilling();
  const { user } = useAuth();
  const isEnterpriseTier = tier === 'enterprise';
  const [contactModalOpen, setContactModalOpen] = useState(false);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Track when the upgrade modal is shown
  useEffect(() => {
    if (isOpen) {
      trackUpgradePromptShown(title, { isPaidPro, tempUnlocked, betaUnlocked });
    }
  }, [isOpen, title, isPaidPro, tempUnlocked, betaUnlocked]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div data-testid="upgrade-modal" className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="px-6 pt-8 pb-6 text-center">
            {/* Icon */}
            {icon && (
              <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {icon}
              </div>
            )}

            {/* Title */}
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {title}
            </h3>

            {/* Description */}
            <p className="text-sm text-gray-600 mb-6">
              {description}
            </p>

            {/* CTA Button */}
            {isEnterpriseTier ? (
              <button
                onClick={() => setContactModalOpen(true)}
                className="block w-full py-3 px-4 text-center text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 rounded-lg transition-colors"
              >
                Contact Sales
              </button>
            ) : (
              <button
                onClick={() => createCheckout.mutate({ billingPeriod: 'monthly', currency: getEffectiveCurrency(undefined, user?.preferredCurrency) })}
                disabled={createCheckout.isPending}
                className="block w-full py-3 px-4 text-center text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createCheckout.isPending ? 'Redirecting...' : buttonText}
              </button>
            )}

            {/* Trust text */}
            <p className="text-xs text-gray-500 mt-3">
              {isEnterpriseTier ? 'Enterprise plans are managed separately' : 'Cancel anytime'}
            </p>
          </div>

          {/* Pro features teaser */}
          <div className="px-6 pb-6">
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-medium text-gray-500 mb-2">
                {isEnterpriseTier ? 'Included in Enterprise:' : 'Included in Pro:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {isEnterpriseTier ? (
                  <>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-600 bg-gray-100">
                      SSO & SCIM
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-600 bg-gray-100">
                      RBAC
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-600 bg-gray-100">
                      Audit Logs
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-600 bg-gray-100">
                      Custom SLA
                    </span>
                  </>
                ) : (
                  <>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-600 bg-gray-100">
                      Health Score
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-600 bg-gray-100">
                      Valuation
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-600 bg-gray-100">
                      24mo history
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-600 bg-gray-100">
                      Public profile
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEnterpriseTier && (
        <ContactSalesModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          source="upgrade-modal"
        />
      )}
    </div>
  );
}
